import { useState, useRef } from 'react';
import {
  MeDto,
  AppSettingsContentResponseModel,
  PersonalInformationScreenPropertiesModel,
  UpdateProfileRequest,
} from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ImagesToImageMap, useClientTranslation } from '@/lib/core';
import { useAuthStore, useLocaleStore } from '@/lib/core/stores';
import { deleteProfileAccount } from '@/services/platform/data/profile';
import { logout } from '@/services/platform/data/authentication';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { EditProfileSheet } from './sheets/edit-profile';
import { EditContactSheet } from './sheets/edit-contact';
import { EditPinSheet } from './sheets/edit-pin';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';
import { formatDateMinimal } from '@/lib/core/utils/dateTime';
import { useApp } from '@/lib/core/providers/appProvider';

function formatPhoneDisplay(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/\s+/g, '');
  const prefix = cleaned.startsWith('+') ? '+' : '';
  const digits = cleaned.replace(/\D/g, '');
  if (!digits) return phone;
  const remainder = digits.length % 3;
  const parts: string[] = [];
  if (remainder > 0) parts.push(digits.slice(0, remainder));
  for (let i = remainder; i < digits.length; i += 3) {
    parts.push(digits.slice(i, i + 3));
  }
  return `${prefix}${parts.join(' ')}`;
}

interface InfoRowProps {
  label: string;
  value: string | null | undefined;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-center gap-2.5 leading-[100%]">
      <p className="font-pp-neue-montreal text-jncquoi-neutral-800 min-w-0 flex-1 text-[14px] tracking-[0.5px]">
        {label}
      </p>
      <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-right text-[14px] tracking-[0.5px]">
        {value ?? '---'}
      </p>
    </div>
  );
}

interface ProfileAccountPersonalInformationProps {
  profile: MeDto | undefined;
  pageProperties: PersonalInformationScreenPropertiesModel | undefined;
  appSettings: AppSettingsContentResponseModel | undefined;
  onSave: (data: UpdateProfileRequest) => Promise<void>;
  onPhotoUpload: (file: File) => Promise<void>;
}

export function ProfileAccountPersonalInformation({
  profile,
  pageProperties,
  appSettings,
  onSave,
  onPhotoUpload,
}: ProfileAccountPersonalInformationProps) {
  const t = useClientTranslation();
  const { user } = useApp();
  const pin = useAuthStore(state => state.pin);
  const locale = useLocaleStore(state => state.locale);
  const hasActiveMembership = user?.hasActiveMembership;
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [isEditPinOpen, setIsEditPinOpen] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profilePhoto = profile?.photoUrl;
  const defaultAvatar = ImagesToImageMap(
    appSettings?.properties?.profileDefaultImage,
    'profileDefaultImg'
  )?.src;
  const profileAvatar =
    profilePhoto && profilePhoto?.includes('http') ? profilePhoto : defaultAvatar || undefined;

  function handleAvatarClick() {
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      await onPhotoUpload(file);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-8 px-4 pt-4 pb-12">
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* Avatar */}
      {hasActiveMembership && (
        <button
          type="button"
          aria-label="Change profile photo"
          disabled={uploading}
          className="relative flex size-34 items-end justify-center overflow-hidden rounded-full pb-4 disabled:cursor-not-allowed"
          onClick={handleAvatarClick}
        >
          {profileAvatar ? (
            <img
              src={profileAvatar}
              alt={profile?.name ?? ''}
              className="absolute inset-0 size-full object-cover"
            />
          ) : (
            <div className="bg-jncquoi-neutral-200 absolute inset-0" />
          )}
          <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent" />
          {uploading ? (
            <SpinLoading
              wrapperClassName="absolute inset-0 min-h-0 bg-black/40"
              spinnerClassName="size-7 border-2 border-white border-b-transparent"
            />
          ) : (
            <Icon name="edit" width={24} height={24} className="relative text-white" />
          )}
        </button>
      )}

      {/* Sections */}
      <div className="flex w-full flex-1 flex-col gap-2">
        {/* Profile */}
        <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b pb-6">
          <div className="flex items-center justify-between">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
              {pageProperties?.profileLabel ?? t('App.Labels.Profile')}
            </p>
            <button
              type="button"
              aria-label={pageProperties?.editProfileTitle ?? 'Edit Profile'}
              className="flex size-11 items-center justify-center"
              onClick={() => setIsEditProfileOpen(true)}
            >
              <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          </div>
          <div className="flex flex-col gap-2.5">
            <InfoRow label={t('App.Labels.Title')} value={profile?.salutation} />
            <InfoRow label={t('App.Labels.FirstName')} value={profile?.firstName} />
            <InfoRow label={t('App.Labels.LastName')} value={profile?.lastName} />
            <InfoRow
              label={t('App.Labels.DateOfBirth')}
              value={formatDateMinimal(profile?.personBirthdate)}
            />
          </div>
        </div>

        {/* Contact information */}
        <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b pb-6">
          <div className="flex items-center justify-between">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
              {pageProperties?.contactInformationLabel ?? t('App.Labels.ContactInformation')}
            </p>
            <button
              type="button"
              aria-label={pageProperties?.editContactInformationTitle ?? 'Edit Contact Information'}
              className="flex size-11 items-center justify-center"
              onClick={() => setIsEditContactOpen(true)}
            >
              <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          </div>
          <div className="flex flex-col gap-2.5">
            <InfoRow label={t('App.Labels.PhoneNumber')} value={formatPhoneDisplay(profile?.phone)} />
            <InfoRow
              label={t('App.Labels.OtherPhoneNumber')}
              value={formatPhoneDisplay(profile?.otherPhone)}
            />
            <InfoRow
              label={t('App.labels.CountryOfResidence')}
              value={
                (profile?.countryOfResidenceName ?? profile?.billingCountry ?? profile?.nationality ?? '') +
                (profile?.countryOfResidenceISO ? ' (' + profile?.countryOfResidenceISO + ')' : '')
              }
            />
          </div>
        </div>

        {/* Login information */}
        <div className="flex flex-col gap-2 pb-4">
          <div className="flex items-center justify-between">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
              {pageProperties?.loginInformationLabel ?? t('App.Labels.LoginInformation')}
            </p>
            <div className="size-11" />
          </div>
          <div className="flex flex-col gap-2.5">
            <InfoRow label={t('App.Labels.Email')} value={profile?.personEmail} />
            {/* Access Pin  */}
            <div className="flex items-center gap-2.5">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-800 min-w-0 flex-1 text-[14px] tracking-[0.5px]">
                {t('App.Labels.AccessPin')}
              </p>
              <button
                type="button"
                aria-label={pageProperties?.resetAccessPINModalTitle ?? t('App.Labels.ResetAccessPin')}
                className="flex shrink-0 items-center gap-2"
                onClick={() => setIsEditPinOpen(true)}
              >
                <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex gap-1 text-[8px]">
                  {pin != null ? '●●●●' : '---'}
                </span>
                <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete account */}
      <Button
        variant="secondary-outline"
        palette="dark"
        size="lg"
        className="w-full"
        onClick={() => setIsDeleteAccountOpen(true)}
      >
        {pageProperties?.requestDeleteAccountBtn ?? t('App.Labels.RequestDeleteAccount')}
      </Button>

      {/* Delete account confirmation sheet */}
      <ConfirmationSheet
        open={isDeleteAccountOpen}
        onOpenChange={setIsDeleteAccountOpen}
        onConfirm={async () => {
          const success = await deleteProfileAccount(locale);
          if (!success) throw new Error('Failed to delete account');
          await logout();
        }}
        headerTitle={pageProperties?.deleteAccountModalTitle ?? t('App.Labels.Confirmation')}
        title={pageProperties?.deleteAccountTitle ?? t('App.Labels.DeleteAccountTitle')}
        confirmLabel={pageProperties?.deleteAccountBtn ?? t('App.Labels.RequestDeleteAccount')}
        dismissLabel={pageProperties?.deleteAccountCancelBtn ?? t('App.Labels.Cancel')}
        successText={t('App.Labels.DeleteAccountSuccess')}
      />

      {/* Edit Profile sheet */}
      <EditProfileSheet
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
        profile={profile}
        pageProperties={pageProperties}
        appSettings={appSettings}
        onSave={onSave}
      />

      {/* Edit PIN sheet */}
      <EditPinSheet
        open={isEditPinOpen}
        onOpenChange={setIsEditPinOpen}
        pageProperties={pageProperties}
        onSave={async () => {}}
      />

      {/* Edit Contact sheet */}
      <EditContactSheet
        open={isEditContactOpen}
        onOpenChange={setIsEditContactOpen}
        profile={profile}
        pageProperties={pageProperties}
        appSettings={appSettings}
        onSave={onSave}
      />
    </div>
  );
}
